import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaPhoneAlt, FaBars, FaTimes, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DEFAULT_SITE_PHONE, useLocalContext } from "../../context/LocalContext";
import brandLogo from "../../assets/Digitalcoreweb Gradient Tech Logo.png";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const phone = webinfo.phone || DEFAULT_SITE_PHONE.localFormat;
  const phoneCall = webinfo.phonecall || DEFAULT_SITE_PHONE.intlFormat;
  const brandName = webinfo.name || "Digitalcoreweb";

  const navItems = useMemo(
    () => [
      { label: "Home", to: "/" },
      { label: "Services", to: "/services" },
      { label: "Strategies", to: "/our-strategies" },
      { label: "About", to: "/about" },
      { label: "Process", to: "/process" },
      { label: "Contact", to: "/contact" },
    ],
    []
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className={`hd4 ${scrolled ? "hd4--scrolled" : ""}`}>
      <div className="hd4-inner">
        <button
          className="hd4-logo"
          onClick={() => navigate("/")}
          aria-label="Go to Homepage"
        >
          <img src={webinfo.logowhite || webinfo.logo || brandLogo} alt={brandName} />
        </button>

        <nav className="hd4-nav" aria-label="Main Navigation">
          {navItems.map((it) => {
            const active = isActive(it.to);
            return (
              <button
                key={it.to}
                className={`hd4-link ${active ? "is-active" : ""}`}
                onClick={() => navigate(it.to)}
              >
                <span>{it.label}</span>
                {active && (
                  <motion.div
                    layoutId="activeNavPill"
                    className="hd4-active-indicator"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <div className="hd4-actions">
          <a className="hd4-call" href={`tel:${phoneCall}`} title="Call us directly">
            <span className="hd4-call-icon">
              <FaPhoneAlt />
            </span>
            <span className="hd4-call-text">{phone}</span>
          </a>

          <button
            className="hd4-cta"
            onClick={() => navigate("/contact")}
            aria-label="Get a Quote"
          >
            <span>Get a Quote</span>
            <FaArrowRight className="hd4-cta-arrow" />
          </button>
        </div>

        <button
          className="hd4-burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* MOBILE DRAWER WITH FRAMER MOTION */}
      <AnimatePresence>
        {menuOpen && (
          <div className="hd4-drawer-root">
            <motion.div
              className="hd4-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMenuOpen(false)}
            />

            <motion.div
              className="hd4-panel"
              initial={{ x: "100%", opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
            >
              <div className="hd4-panel-top">
                <div className="hd4-drawer-brand">
                  <img
                    src={webinfo.logowhite || webinfo.logo || brandLogo}
                    alt={brandName}
                    className="hd4-drawer-logo-img"
                  />
                </div>
                <button
                  className="hd4-close"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <FaTimes />
                </button>
              </div>

              <nav className="hd4-mnav">
                {navItems.map((it, idx) => {
                  const active = isActive(it.to);
                  return (
                    <motion.button
                      key={it.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * idx, duration: 0.2 }}
                      className={`hd4-mitem ${active ? "is-active" : ""}`}
                      onClick={() => {
                        setMenuOpen(false);
                        navigate(it.to);
                      }}
                    >
                      <span>{it.label}</span>
                      <FaArrowRight className="hd4-marrow" />
                    </motion.button>
                  );
                })}
              </nav>

              <div className="hd4-mcta">
                <a className="hd4-mcall" href={`tel:${phoneCall}`}>
                  <FaPhoneAlt /> {phone}
                </a>
                <button
                  className="hd4-mprimary"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/contact");
                  }}
                >
                  <span>Get a Free Quote</span>
                  <FaArrowRight />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;